import React, { useCallback, useMemo, useState, useEffect } from 'react';
import '../../styles/carousel/carousel.scss';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Carousel from './Carousel';
import Selector from './Selector';
import { companyOptions } from './Options';
import initialHouses from '../../data/houses.json';

const normalizeAddressLike = (s) => {
    return String(s || '')
        .toLowerCase()
        .replace(/[.,#]/g, ' ')
        .replace(/\b(road|rd|drive|dr|street|st|avenue|ave|lane|ln|trail|trl|court|ct|circle|cir|boulevard|blvd)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const bestHouseMatch = (propertyName, sheetHouses) => {
    const propNorm = normalizeAddressLike(propertyName);
    if (!propNorm) return null;

    const propTokens = new Set(propNorm.split(' '));
    const propNumber = propNorm.match(/^\d+/)?.[0] ?? null;

    let best = null;
    for (const h of sheetHouses) {
        const hNorm = normalizeAddressLike(h.house);
        if (!hNorm) continue;

        const hNumber = hNorm.match(/^\d+/)?.[0] ?? null;
        let score = 0;
        if (propNumber && hNumber && propNumber === hNumber) score += 10;

        for (const t of hNorm.split(' ')) {
            if (propTokens.has(t)) score += 1;
        }

        if (!best || score > best.score) best = { match: h, score };
    }

    // Require some minimal overlap so we don't mis-assign availability.
    if (!best || best.score < 6) return null;
    return best.match;
};

const CarouselSection = () => {
    const location = useLocation();

    const [index, setIndex] = useState(0);
    const [houses, setHouses] = useState(initialHouses);
    
    useEffect(() => {
        const endpoint = process.env.REACT_APP_AVAILABILITY_ENDPOINT || '/api/availability';
        const controller = new AbortController();

        (async () => {
            try {
                const resp = await fetch(endpoint, { signal: controller.signal });
                if (!resp.ok) return;
                const data = await resp.json();
                const sheetHouses = Array.isArray(data?.houses) ? data.houses : [];
                if (sheetHouses.length === 0) return;

                setHouses((prev) => {
                    return prev.map((h) => {
                        const match = bestHouseMatch(h.property_name, sheetHouses);
                        const availabilityText = match?.availabilityText;
                        return availabilityText ? { ...h, availabilityText } : h;
                    });
                });
            } catch (e) {
                // Best-effort: keep existing hardcoded availability on any failure.
                console.warn('Availability fetch failed', e);
            }
        })();

        return () => controller.abort();
    }, []);

    // For selector
    const defaultCompanyOption = companyOptions.find((item) => item.value === 'tesla') || companyOptions[0];
    const [selectedOption, setSelectedOption] = useState(defaultCompanyOption);
    const filteredHouses = useMemo(() => {
        if (selectedOption.value === 'all') return houses;
        return houses
            .filter((house) => house.companies.includes(selectedOption.value))
            .sort((a, b) => a.companies.indexOf(selectedOption.value) - b.companies.indexOf(selectedOption.value));
    }, [houses, selectedOption.value]);
    const length = filteredHouses.length;

    const findIndexByLabel = useCallback((label) => {
        return companyOptions.findIndex((item) => item.label.toLowerCase() === String(label || '').toLowerCase());
    }, []);

    const handleChange = useCallback((nextSelectedOption) => {
        setIndex(0);
        setSelectedOption(nextSelectedOption);
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlValue = searchParams.get('company');
        if (!urlValue) return;

        const idx = findIndexByLabel(urlValue);
        handleChange(idx === -1 ? defaultCompanyOption : companyOptions[idx]);
    }, [defaultCompanyOption, findIndexByLabel, handleChange, location.search]);

    const fireGtag = useCallback(
        (eventName) => {
            const houseName = filteredHouses[index]?.houseName;
            if (!houseName) return;
            if (typeof window.gtag !== 'function') return;
            window.gtag('event', eventName, {
                transaction_id: "T" + Math.floor(Math.random() * 1000001),
                house_name: houseName,
            });
        },
        [filteredHouses, index],
    );

    const handlePrevious = () => {
        const newIndex = index - 1;
        setIndex(newIndex < 0 ? length - 1 : newIndex);
        console.log(index);
    };

    const handleNext = () => {
        const newIndex = index + 1;
        setIndex(newIndex >= length ? 0 : newIndex);
        console.log(index);
    };

    if (length === 0) return null;

    return (
        <div id='availability-layout'>
        <div id='carousel-layout'>
            <div id='carousel-title-and-selector'>
                <h2 style={{ marginRight: '10px' }} id='carousel-title'>{length} Intern House{length === 1 ? '':'s'} in Austin, TX near </h2>
                <Selector selectedOption={selectedOption} handleChange={handleChange}/>
            </div>
            
            <Carousel houses={filteredHouses} index={index} length={length} handlePrevious={handlePrevious} handleNext={handleNext}/>

            <section id='carousel-info-section'>
                <section id='carousel-title-section'>
                    <h1 id='carousel-house-title'>{filteredHouses[index].houseName}</h1>
                    <h2 id='carousel-bed-bath'>{filteredHouses[index].bedBath}</h2>
                </section>
                <ul id='info-list'>
                    {filteredHouses[index].info.map((el, key = -1) => {
                        key += 1;
                        return <li key={key} className='list-items'>{el}</li>
                    })}
                </ul>
            </section>
            <section id='carousel-button-section'>
                { filteredHouses[index].tourLink !== '' &&
                    <motion.a whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    whileInView={{ opacity: 1 }} onClick={() => fireGtag('click_3d_tour')} id='3d-tour-button' className='carousel-button' href={filteredHouses[index].tourLink} target='_blank' rel='noopener noreferrer'>{filteredHouses[index].tourOrPictures === 'pictures' ? "Pictures" : "3D Tour"}</motion.a>
                }
                
                <motion.a whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    whileInView={{ opacity: 1 }} onClick={() => fireGtag('click_apply')} id='apply-button' className='carousel-button' href={filteredHouses[index].applyLink} target='_blank' rel='noopener noreferrer'>Apply</motion.a>
            </section>
        </div>
        </div>
    );
}

export default CarouselSection;
